/**
 * translate — the pure, Node+browser-safe catalog lookup shared by the
 * legal-document builders (`legal.ts`) and, via the schmancy `LocalizeMixin`,
 * the browser. No DOM, no globals, no mutable module state — every call is
 * a function of its `locale` argument.
 *
 * This is the UI-language axis (which catalog renders) — distinct from the
 * BCP-47 formatting locale `Intl` reads (see `@mhmo91/rx`'s
 * `jurisdictionToLocale`). The two never collapse: a `de`/`en` UI choice
 * maps outward to a region-complete tag wherever formatting is needed, never
 * the bare two-letter UI tag.
 */
import { jurisdictionToLocale } from "@mhmo91/rx";
import { z } from "zod";
import { de } from "./messages.de.ts";
import { en } from "./messages.en.ts";

/**
 * LocaleEnum — the single source for the two-token UI-language vocabulary.
 * Every field that carries a UI language (`Order.LocaleSchema`,
 * `SendOTPInput.locale`, `CreateInviteInput.locale`, …) references this enum
 * rather than re-listing `['de', 'en']`; `Locale` is its `z.infer` so the
 * runtime enum and the compile-time type can never drift apart.
 */
export const LocaleEnum = z.enum(["de", "en"]);
export type Locale = z.infer<typeof LocaleEnum>;

/**
 * localeEndonym — each UI language's name in ITS OWN language: the display
 * form of a `Locale` value, never copy. A language picker exists to be found
 * by someone who cannot read the language currently on screen, so its options
 * are not translated — "Deutsch" stays "Deutsch" on an English page. A third
 * locale adds its endonym here, beside the enum that defines the set, where
 * `Record<Locale, string>` makes omitting it a compile error.
 */
export const localeEndonym: Record<Locale, string> = {
	de: "Deutsch",
	en: "English",
};

type Leaf = string | readonly string[];

// Not exported — MessageKey is the public surface; NestedKeyOf is the
// mechanism that derives it from `en`'s literal shape.
type NestedKeyOf<T> = {
	[K in Extract<keyof T, string>]: T[K] extends string
		? K
		: T[K] extends readonly string[]
			? K
			: T[K] extends Record<string, unknown>
				? `${K}.${NestedKeyOf<T[K]>}`
				: never;
}[Extract<keyof T, string>];

/** Compile-time union of every dotted catalog key — `en` is the source-of-truth. */
export type MessageKey = NestedKeyOf<typeof en>;

// The array leaves — the legal corpus's paragraph runs — are laid out by the
// document builders, which take the whole array. A component's `t()` renders
// one string, so the keys it accepts are the string leaves alone.
type StringLeafKeyOf<T> = {
	[K in Extract<keyof T, string>]: T[K] extends string
		? K
		: T[K] extends readonly string[]
			? never
			: T[K] extends Record<string, unknown>
				? `${K}.${StringLeafKeyOf<T[K]>}`
				: never;
}[Extract<keyof T, string>];

/** The catalog keys a view renders through `t()` — every leaf that is ONE
 *  string. Narrower than `MessageKey` by exactly the legal corpus's arrays. */
export type TextKey = StringLeafKeyOf<typeof en>;

// Dotted-key walk over an active-locale catalog object. Returns undefined on
// any missing segment (mistyped key, or a de/en drift a bug slipped past the
// CatalogShape check) — translate() turns that into "return the key", never
// a throw.
function resolve(key: string, locale: Locale): Leaf | undefined {
	let node: unknown = locale === "de" ? de : en;
	for (const segment of key.split(".")) {
		if (typeof node !== "object" || node === null || !(segment in node))
			return undefined;
		node = (node as Record<string, unknown>)[segment];
	}
	return typeof node === "string" || Array.isArray(node) ? node : undefined;
}

// Single-pass replace — a param VALUE that itself contains another param's
// `{{token}}` (e.g. `{ name: '{{email}}', email: 'real@x.com' }`) must never
// be re-scanned for substitution. A sequential per-param `.split/.join` over
// the accumulator does exactly that; matching every `{{token}}` in one regex
// pass against the original text (not the growing accumulator) avoids it.
// A token with no matching param is left as the literal `{{token}}`.
function interpolate(
	text: string,
	params: Record<string, string | number> | undefined,
): string {
	if (!params) return text;
	return text.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
		name in params ? String(params[name]) : match,
	);
}

/**
 * translate — dotted-key catalog lookup + `{{param}}` interpolation. Pure:
 * no throw, no I/O, no global locale. A missing key returns the key string
 * itself (never throws) so a mistranslated or not-yet-seeded string never
 * blocks a legal-document render.
 */
export function translate(
	locale: Locale,
	key: MessageKey,
	params?: Record<string, string | number>,
): string {
	const value = resolve(key, locale);
	if (value === undefined) return key;
	const text = typeof value === "string" ? value : value.join("\n");
	return interpolate(text, params);
}

/**
 * translateList — the list-leaf counterpart to `translate`: a catalog key
 * whose value is a `readonly string[]` (a bulleted list) resolves here as
 * its raw item array, each item interpolated independently, so a consumer
 * that must render a real `<ul><li>` (this repo's `legal-document.view.ts`)
 * never receives `translate`'s newline-joined flattening, which exists for
 * plain-text consumers instead. Absent from the momo-starter reference
 * translate.ts, which has no structured-list consumer of its own — see this
 * package's deviations note.
 */
export function translateList(
	locale: Locale,
	key: MessageKey,
	params?: Record<string, string | number>,
): string[] {
	const value = resolve(key, locale);
	if (value === undefined) return [key];
	const items = typeof value === "string" ? [value] : value;
	return items.map((item) => interpolate(item, params));
}

/**
 * uiLanguageFromCountry — the recipient-locale resolver for outbound
 * documents: a counterparty's country reduces to the UI language its
 * document renders in. Reuses `jurisdictionToLocale` (@mhmo91/rx) rather than
 * re-deriving a country→locale map. Absent or unmapped country is the
 * terminal `'de'` case — the app's default language — never a guess at `'en'`.
 */
export function uiLanguageFromCountry(
	country: string | null | undefined,
): Locale {
	const bcp47 = country ? jurisdictionToLocale(country) : undefined;
	if (!bcp47) return "de";
	return bcp47.startsWith("de") ? "de" : "en";
}

/**
 * uiLanguageFromBcp47 — reduces a BCP-47 tag (e.g. the sign-in view's own
 * `navigator.language`) to the UI language axis. A tag outside {de, en} —
 * or an absent one — falls back to the caller-supplied `fallback`.
 */
export function uiLanguageFromBcp47(
	locale: string | null | undefined,
	fallback: Locale,
): Locale {
	if (!locale) return fallback;
	const lower = locale.toLowerCase();
	if (lower.startsWith("de")) return "de";
	if (lower.startsWith("en")) return "en";
	return fallback;
}
